/**
 * Lazy per-shop daily detail behind the shops table's expandable rows (owner
 * ruling 2026-08-12). State lives here, ABOVE the expanded row, so collapsing
 * and re-expanding a shop never refetches -- one request per shop per period.
 * Changing the period drops every cached entry, since the rows are scoped to
 * the selected period.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import type { ReportRow } from '../../api/index.ts';
import { useAuth } from '../../auth/AuthContext.tsx';
import { actionError } from '../../components/actionError.ts';

/** v1's "last 7 days" glance; GET /reports is already newest-first. */
export const DAILY_DETAIL_LIMIT = 7;

export type ShopDailyDetail =
  | { kind: 'loading' }
  | { kind: 'ready'; rows: ReportRow[] }
  | { kind: 'error'; message: string };

export interface UseShopDailyDetailsOptions {
  year: number;
  /** null = whole year (the period control's All-year mode). */
  month: number | null;
  reportType?: string;
}

export interface UseShopDailyDetailsResult {
  details: ReadonlyMap<number, ShopDailyDetail>;
  /** First call per shop fetches; later calls are no-ops. */
  load: (shopId: number) => void;
}

export function useShopDailyDetails({
  year,
  month,
  reportType,
}: UseShopDailyDetailsOptions): UseShopDailyDetailsResult {
  const { api } = useAuth();
  const [details, setDetails] = useState<Map<number, ShopDailyDetail>>(new Map());
  // Mirrors `details` for the load guard: reading state inside the callback
  // would need it as a dependency, re-creating `load` on every fetch.
  const requestedRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    requestedRef.current = new Set();
    setDetails(new Map());
  }, [year, month, reportType]);

  const load = useCallback(
    (shopId: number): void => {
      if (requestedRef.current.has(shopId)) return;
      requestedRef.current.add(shopId);
      const requestedFor = { year, month, reportType };
      setDetails((prev) => new Map(prev).set(shopId, { kind: 'loading' }));
      api.reports
        .list({
          shop_id: shopId,
          year,
          month: month ?? undefined,
          report_type: reportType,
          page: 1,
          limit: DAILY_DETAIL_LIMIT,
        })
        .then((res) => {
          // A period change between request and response cleared the cache;
          // dropping the stale result keeps the two in step.
          if (requestedFor.year !== year || requestedFor.month !== month) return;
          setDetails((prev) => new Map(prev).set(shopId, { kind: 'ready', rows: res.data }));
        })
        .catch((e: unknown) => {
          requestedRef.current.delete(shopId);
          setDetails((prev) => new Map(prev).set(shopId, { kind: 'error', message: actionError(e) }));
        });
    },
    [api, year, month, reportType],
  );

  return { details, load };
}

export default useShopDailyDetails;
